<template>
  <div class="certificate-container">
    <!-- 左侧导航 - 使用统一的侧边栏组件 -->
    <SidebarMenu />
    

    <!-- 右侧主内容 -->
    <div class="main-content">
      <div class="content-header">
        <h2>二维码信息</h2>
        <el-button @click="handleLogout" type="text" style="margin-left: auto;">退出登录</el-button>
      </div>

      <!-- 搜索条件区域 -->
      <div class="search-area">
        <el-form :inline="true" class="search-form">
          <el-form-item label="证书编号">
            <el-input v-model="searchForm.certificateCode" placeholder="请输入"></el-input>
          </el-form-item>
          <el-form-item label="最近更新时间">
            <el-date-picker
              v-model="searchForm.timeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              style="width: 400px;"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSearch">查询</el-button>
            <el-button @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>

        <!-- 操作按钮区域 -->
        <div class="action-buttons">
          <el-button type="primary" plain @click="handleImport">导入信息</el-button>
          <el-button type="primary" plain @click="handleDownloadAll">批量下载二维码</el-button>
          <el-button type="primary" plain @click="handleExport">导出Excel</el-button>
          <el-button type="danger" plain @click="handleBatchDelete" :disabled="selectedRows.length === 0">批量删除</el-button>
        </div>
      </div>

      <!-- 表格区域 -->
      <div class="table-area">
        <div class="table-wrapper">
          <el-table
            v-loading="loading"
            :data="certificateList"
            style="width: 100%"
            @selection-change="handleSelectionChange"
            @row-click="handleRowClick"
          >
            <el-table-column type="selection" min-width="60"></el-table-column>
            <el-table-column type="index" label="序号" min-width="60"></el-table-column>
            <el-table-column prop="certificateCode" label="证书编号" min-width="120"></el-table-column>
            <!-- <el-table-column prop="certificateCompanyName" label="证书单位" min-width="150"></el-table-column>
            <el-table-column prop="instrumentName" label="器具名称" min-width="150"></el-table-column>
            <el-table-column prop="modelSpecification" label="型号规格" min-width="120"></el-table-column>
            <el-table-column prop="factorySerialCode" label="出厂编号" min-width="120"></el-table-column> -->
            <el-table-column prop="updateTime" label="最近更新时间" min-width="150"></el-table-column>
            <el-table-column label="操作" min-width="150" fixed="right">
              <template #default="scope">
                <el-button type="text" @click="handleEdit(scope.row)">修改</el-button>
                <el-button type="text" @click="handleDownload(scope.row)">下载二维码</el-button>
                <el-button type="text" danger @click="handleDelete(scope.row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <!-- 横向滚动指示器 -->
          <div class="scroll-indicator" ref="scrollIndicator">← 左右拖拽查看更多 →</div>
        </div>
      </div>

      <!-- 分页区域 -->
      <div class="pagination-area">
        <Pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
        />
      </div>

      <!-- 页脚 -->
      <Footer />
    </div>
  </div>
  
  <!-- 隐藏的文件输入元素，用于导入功能 -->
  <input 
    ref="fileInputRef" 
    type="file" 
    accept=".xlsx,.xls,.csv" 
    style="display: none;" 
  />

  <!-- 修改对话框子组件 -->
  <CertificateEditDialog
    v-model:visible="dialogVisible"
    :certificate-id="currentCertificateId"
    :row-data="currentRowData"
    @saved="handleEditSaved"
  />

  <!-- 批量删除确认对话框 -->
  <el-dialog
    v-model="batchDeleteVisible"
    title="确认批量删除"
    width="400px"
    :before-close="handleBatchDeleteCancel"
  >
    <div style="text-align: center; padding: 20px 0;">
      <p>确定要删除选中的 {{ selectedRows.length }} 条证书吗？</p>
      <p style="color: #f56c6c; margin-top: 10px;">此操作不可撤销，请谨慎操作！</p>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleBatchDeleteCancel">取消</el-button>
        <el-button type="danger" @click="handleBatchDeleteConfirm">确定删除</el-button>
      </span>
    </template>
  </el-dialog>
  
  <!-- 可拖拽的详情抽屉组件 -->
  <CertificateDetailDrawer
    :visible="detailDrawerVisible"
    :current-row="currentDetailRow"
    @close="handleDetailDrawerClose"
    @edit="handleEdit"
    @download="handleDownload"
  />
</template>

<script setup>
import { reactive, ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox, ElLoading } from 'element-plus'
import certificateAPI from '@/api/certificate.js'
import SidebarMenu from '@/components/SidebarMenu.vue'
import CertificateEditDialog from './components/CertificateEditDialog.vue'
import Pagination from '@/components/Pagination.vue'
import Footer from '@/components/Footer.vue'
import CertificateDetailDrawer from './components/CertificateDetailDrawer.vue'

const router = useRouter()

// 搜索表单数据
const searchForm = reactive({
  certificateCode: '',
  timeRange: null
})

// 表格数据和状态
const certificateList = ref([])
const loading = ref(false)
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(10)

// 选中的行数据
const selectedRows = ref([])



// 修改对话框相关
const dialogVisible = ref(false)
const currentCertificateId = ref(0) // Initialize with 0 instead of null to meet Number type requirement
// 当前要编辑的行数据
const currentRowData = ref(null)
// 批量删除对话框相关
const batchDeleteVisible = ref(false)

// 文件输入引用，用于导入功能
const fileInputRef = ref(null)

// 详情抽屉相关
const detailDrawerVisible = ref(false)
const currentDetailRow = ref(null)

// 滚动指示器引用
const scrollIndicator = ref(null)

// 页面加载时获取数据
onMounted(() => {
  loadData()
  
  // 为文件输入元素添加事件监听器
  if (fileInputRef.value) {
    fileInputRef.value.onchange = async (event) => {
      const file = event.target.files[0]
      if (!file) return
      
      try {
        const loadingInstance = ElLoading.service({
          text: '正在导入信息，请稍候...'
        })
        
        // 调用API导入证书信息
        const importResult = await certificateAPI.importCertificates(file)
        
        ElMessage.success(`导入成功，等待后台解析内容,可在导入历史记录查询状态`)
        
        // 刷新列表
        loadData()
        
        // 清空文件输入
        fileInputRef.value.value = ''
      } catch (error) {
        ElMessage.error('导入失败，请稍后重试')
        console.error('导入证书信息失败:', error)
      } finally {
        ElLoading.service().close()
      }
    }
  }
  
  // 页面加载后和窗口大小改变时检查表格是否需要滚动
  setTimeout(() => {
    checkTableScroll()
  }, 100)
  
  window.addEventListener('resize', handleResize)
  
  // 监听表格容器滚动事件
  const tableWrapper = document.querySelector('.table-wrapper')
  if (tableWrapper) {
    tableWrapper.addEventListener('scroll', handleTableScroll)
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  const tableWrapper = document.querySelector('.table-wrapper')
  if (tableWrapper) {
    tableWrapper.removeEventListener('scroll', handleTableScroll)
  }
})

// 检查表格是否需要横向滚动
const checkTableScroll = () => {
  const tableWrapper = document.querySelector('.table-wrapper')
  if (tableWrapper && scrollIndicator.value) {
    // 检查内容是否超出容器宽度
    const shouldShowIndicator = tableWrapper.scrollWidth > tableWrapper.clientWidth
    scrollIndicator.value.style.display = shouldShowIndicator ? 'block' : 'none'
  }
}

// 处理窗口大小变化
const handleResize = () => {
  checkTableScroll()
}

// 处理表格滚动
const handleTableScroll = (event) => {
  const tableWrapper = event.target
  if (scrollIndicator.value) {
    // 当用户滚动时隐藏指示器，一段时间后再显示
    scrollIndicator.value.style.display = 'none'
    
    clearTimeout(window.scrollIndicatorTimeout)
    window.scrollIndicatorTimeout = setTimeout(() => {
      checkTableScroll()
    }, 3000)
  }
}

// 加载数据
const loadData = async () => {
  loading.value = true
  try {
    // 构建查询参数，不直接展开searchForm以避免传入timeRange
    const params = {
      pageNo: currentPage.value,
      pageSize: pageSize.value,
      certificateCode: searchForm.certificateCode
    }
    
    // 如果有时间范围，需要转换格式并添加到params
    if (searchForm.timeRange && searchForm.timeRange.length === 2) {
      // 自定义formatDate函数保持当地时间不变
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };
      
      params.startTime = formatDate(searchForm.timeRange[0]);
      params.endTime = formatDate(searchForm.timeRange[1]);
      console.log('转换后的startTime:', params.startTime);
      console.log('转换后的endTime:', params.endTime);
    }

    console.log('最终构建的请求参数:', params);
    
    // 调用API获取证书列表
    const response = await certificateAPI.getCertificateList(params)
    
    // 初始化默认值
    let listData = []
    let totalCount = 0
    
    // 根据api.js的响应拦截器逻辑，当业务逻辑成功(code=0)时，会直接返回result.data
    // 因此我们需要根据实际返回的数据结构进行处理
    if (response && typeof response === 'object') {
      // 根据certificate.js的API文档，getCertificateList接口返回的完整数据结构应该是
      // {code: number, msg: string, data: {pageSize: string, pageNo: string, count: string, data: [...]}}
      // 但由于响应拦截器的处理，我们这里可能已经直接拿到了result.data部分
      
      if (Array.isArray(response)) {
        // 情况1: response直接是数组（可能是后端直接返回了数组，或者响应拦截器做了特殊处理）
        listData = response
        totalCount = response.length
      } else if (Array.isArray(response.data)) {
        // 情况2: response是一个对象，其中data属性是数组
        listData = response.data
        totalCount = Number(response.count)
      } else if (response.pageSize !== undefined && response.data !== undefined) {
        // 情况3: response是分页对象（符合API文档的格式，包含pageSize, pageNo, count, data等字段）
        listData = Array.isArray(response.data) ? response.data : []
        // 确保totalCount始终是数字类型
        totalCount = Number(response.count) || listData.length || 0
      }
      // 其他情况保持默认值
    }
    
    // 确保certificateList始终是一个数组，防止element-plus表格组件报错
    certificateList.value = Array.isArray(listData) ? listData : []
    total.value = totalCount
    
    // 清空选中项
    selectedRows.value = []
  } catch (error) {
    ElMessage.error('获取数据失败，请稍后重试')
    console.error('获取证书列表失败:', error)
  } finally {
    loading.value = false
  }
}

// 处理查询
const handleSearch = () => {
  currentPage.value = 1
  loadData()
}

// 处理重置
const handleReset = () => {
  searchForm.certificateCode = ''
  searchForm.timeRange = null
  ElMessage.info('搜索条件已重置')
}

// 处理退出登录
const handleLogout = () => {
  ElMessageBox.confirm('确定要退出登录吗？', '确认', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    // 清除登录状态
    localStorage.removeItem('isLoggedIn')
    localStorage.removeItem('username')
    
    // 显示退出成功提示
    ElMessage.success('退出登录成功')
    
    // 跳转到登录页面
    router.push('/')
  }).catch(() => {
    ElMessage.info('已取消退出登录')
  })
}

// 处理选中行变化
const handleSelectionChange = (selection) => {
  selectedRows.value = selection
}

// 处理页码变化
const handleCurrentChange = (page) => {
  currentPage.value = page
  loadData()
}

// 处理上一页
const handlePrev = () => {
  if (currentPage.value > 1) {
    currentPage.value = currentPage.value - 1
    loadData()
  }
}

// 处理下一页
const handleNext = () => {
  if (currentPage.value * pageSize.value < total.value) {
    currentPage.value = currentPage.value + 1
    loadData()
  }
}

// 处理每页条数变化
const handleSizeChange = (size) => {
  pageSize.value = size
  loadData()
}

// 打开修改对话框
const handleEdit = async (row) => {
  console.log('handleEdit调用:', row)
  // 检查row对象和certificateId字段
  if (!row) {
    console.error('handleEdit: 无效的行数据')
    ElMessage.error('数据错误，请刷新页面后重试')
    return
  }
  
  // 优先使用certificateId，如果不存在则尝试使用其他可能的ID字段
  const certificateId = row.certificateId || row.id || row.certificate_no
  console.log('获取到的证书ID:', certificateId, '字段来源:', 
    row.certificateId !== undefined ? 'certificateId' : 
    row.id !== undefined ? 'id' : 'certificate_no')
  
  // 检查证书ID是否为有效数字
  const numericId = parseInt(certificateId)
  if (isNaN(numericId) || numericId <= 0) {
    console.error('handleEdit: 无效的证书ID:', certificateId)
    ElMessage.error('证书ID无效，请刷新页面后重试')
    return
  }
  
  try {
    // 设置一个计时器，3秒后显示加载提示
    let loadingMessage = null
    const loadingTimeout = setTimeout(() => {
      loadingMessage = ElMessage({ message: '正在加载证书信息...', type: 'info', duration: 0 })
    }, 3000)
    
    try {
      // 先调用API获取证书详情，确保数据存在
      await certificateAPI.getCertificateById(numericId)
      
      // 清除计时器，防止API快速返回后仍显示加载提示
      clearTimeout(loadingTimeout)
      // 如果加载提示已显示，则关闭它
      if (loadingMessage) {
        loadingMessage.close()
      }
      
      // 保存当前行数据
      currentRowData.value = JSON.parse(JSON.stringify(row))
      // 设置当前要编辑的证书ID
      currentCertificateId.value = numericId
      console.log('currentCertificateId设置为:', currentCertificateId.value)
      // 打开对话框
      dialogVisible.value = true
    } catch (apiError) {
      // 清除计时器
      clearTimeout(loadingTimeout)
      // 关闭可能已显示的加载提示
      if (loadingMessage) {
        loadingMessage.close()
      }
      // 重新抛出错误，让外层catch处理
      throw apiError
    }
  } catch (error) {
    // 如果是用户取消，则不显示错误信息
    if (error !== 'cancel') {
      ElMessage.error('加载证书信息失败，请稍后重试')
      console.error('加载证书信息失败:', error)
    }
  }
}

// 处理编辑保存完成
const handleEditSaved = () => {
  ElMessage.success('证书信息修改成功')
  loadData()
  dialogVisible.value = false
}

// 处理删除
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除证书编号：${row.certificateCode} 吗？`,
      '提示',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    const loadingInstance = ElLoading.service({
      text: '正在删除，请稍候...'
    })
    
    // 调用API删除证书
    await certificateAPI.deleteCertificate(row.id)
    
    ElMessage.success('删除成功')
    
    // 刷新列表
    loadData()
  } catch (error) {
    // 如果是用户取消，则不显示错误信息
    if (error !== 'cancel') {
      ElMessage.error('删除失败，请稍后重试')
      console.error('删除证书失败:', error)
    }
  } finally {
    ElLoading.service().close()
  }
}

// 处理批量删除按钮点击
const handleBatchDelete = () => {
  if (selectedRows.value.length === 0) {
    ElMessage.warning('请先选择要删除的证书')
    return
  }
  batchDeleteVisible.value = true
}

// 处理批量删除确认
const handleBatchDeleteConfirm = async () => {
  try {
    const loadingInstance = ElLoading.service({
      text: '正在批量删除，请稍候...'
    })
    
    // 获取选中行的ID列表
    const ids = selectedRows.value.map(row => row.id)
    
    // 调用API批量删除证书
    await certificateAPI.batchDeleteCertificates(ids)
    
    ElMessage.success(`成功删除 ${selectedRows.value.length} 条证书信息`)
    
    // 刷新列表
    loadData()
    
    // 关闭对话框
    batchDeleteVisible.value = false
  } catch (error) {
    ElMessage.error('批量删除失败，请稍后重试')
    console.error('批量删除证书失败:', error)
  } finally {
    ElLoading.service().close()
  }
}

// 处理批量删除取消
const handleBatchDeleteCancel = () => {
  batchDeleteVisible.value = false
}

// 处理导入信息
const handleImport = () => {
  // 触发文件选择对话框
  if (fileInputRef.value) {
    fileInputRef.value.click()
  }
}

// 处理导出Excel
const handleExport = async () => {
  // 将loadingInstance声明移到try块外部
  const loadingInstance = ElLoading.service({
    text: '正在导出Excel，请稍候...'
  })
  
  try {
    // 构建查询参数
    const params = {
      certificateCode: searchForm.certificateCode
    }
    
    // 如果有选中的行，添加选中的id集合到参数中
    if (selectedRows.value && selectedRows.value.length > 0) {
      params.ids = selectedRows.value.map(row => row.id)
    }
    
    // 如果有时间范围，需要转换格式并添加到params
    if (searchForm.timeRange && searchForm.timeRange.length === 2) {
      // 自定义formatDate函数保持当地时间不变
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };
      
      params.startTime = formatDate(searchForm.timeRange[0]);
      params.endTime = formatDate(searchForm.timeRange[1]);
    }
    
    // 调用API导出证书信息
    const response = await certificateAPI.exportExcel(params)
    
    // 创建下载链接
    const blob = new File([response], `证书信息_${new Date().getTime()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `证书信息_${new Date().getTime()}.xlsx`
    
    // 不需要将link添加到DOM中，可以直接触发点击
    link.click()
    
    // 只需要释放URL对象，不需要移除link元素
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('导出成功')
  } catch (error) {
    // 显示错误信息
    ElMessage.error(error.message || '导出失败，请稍后重试')
    console.error('导出证书信息失败:', error)
  } finally {
    // 无论成功失败，都关闭加载提示
    loadingInstance.close()
  }
}

// 处理下载单个二维码
const handleDownload = async (row) => {
  try {
    const loadingInstance = ElLoading.service({
      text: '正在下载二维码，请稍候...'
    })
    
    // 调用API下载二维码
    const response = await certificateAPI.downloadQrCode(row.id)
    
    // 创建下载链接
    const blob = response
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `二维码_${row.certificateCode}.png`
    link.click()
    
    // 释放URL对象
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('二维码下载成功')
  } catch (error) {
    // 直接显示API返回的错误消息
    ElMessage.error(error.message || '下载失败，请稍后重试')
    console.error('下载二维码失败:', error)
  } finally {
    ElLoading.service().close()
  }
}

// 处理下载所有二维码
const handleDownloadAll = async () => {
  try {
    let ids = []
    
    // 确定要下载的ID列表
    if (selectedRows.value.length > 0) {
      ids = selectedRows.value.map(row => row.id)
    } else {
      // 如果没有选中行，则下载当前页的所有二维码
      ids = certificateList.value.map(row => row.id)
    }
    
    if (ids.length === 0) {
      ElMessage.warning('没有可下载的二维码')
      return
    }
    
    const loadingInstance = ElLoading.service({
      text: `正在下载 ${ids.length} 个二维码，请稍候...`
    })
    
    // 调用API批量下载二维码（注意：按照新接口格式，需要将ids包装在params对象中）
    const response = await certificateAPI.batchDownloadQrCodes({ ids })
    
    // 创建下载链接
    const blob = response
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `二维码批量下载_${new Date().getTime()}.zip`
    link.click()
    
    // 释放URL对象
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('二维码批量下载成功')
  } catch (error) {
    // 直接显示API返回的错误消息
    ElMessage.error(error.message || '下载失败，请稍后重试')
    console.error('批量下载二维码失败:', error)
  } finally {
    ElLoading.service().close()
  }
}

// 处理行点击事件，显示详情抽屉
const handleRowClick = (row) => {
  currentDetailRow.value = row
  detailDrawerVisible.value = true
}

// 处理详情抽屉关闭
const handleDetailDrawerClose = () => {
  detailDrawerVisible.value = false
  currentDetailRow.value = null
}
</script>

<style scoped>
/* 导入共享样式 */
@import '@/style/shared-styles.css';

/* 证书列表页面特有的样式 */
.search-area {
  padding: 20px 30px;
  background-color: var(--bg-primary);
  margin: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.action-buttons {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
}

/* 分页样式 */
.pagination-area {
  margin-top: 20px;
}

.simple-pagination {
  margin-top: 20px;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
}

.pagination-info {
  color: #606266;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pagination-controls button {
  padding: 4px 12px;
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #606266;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.pagination-controls button:hover:not(:disabled) {
  color: #409eff;
  border-color: #c6e2ff;
  background: #ecf5ff;
}

.pagination-controls button:disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}

.current-page {
  min-width: 24px;
  text-align: center;
  font-weight: 500;
  color: #409eff;
}

/* 响应式布局 - 证书列表特有样式 */
@media screen and (max-width: 992px) {
  /* 隐藏部分不那么重要的列 */
  :deep(.el-table-column[prop='factoryNo']) {
    display: none;
  }
}
</style>